import { useCallback, useEffect, useState } from 'react'
import { Check, Loader2, Plus, Send, TestTube2, Trash2 } from 'lucide-react'
import { useNotification } from '../../../contexts/NotificationContext'
import {
  DEFAULT_AUDIENCE,
  cancelCampaign,
  createCampaign,
  deleteCampaign,
  fetchCampaign,
  fetchCampaigns,
  sendCampaign,
  sendTestEmail,
  updateCampaign,
} from '../../../services/crmService'
import { sanitizeEmailHtml } from '../../../lib/emailHtml'
import { Badge } from '../../ui/badge'
import { Button } from '../../ui/button'
import { ConfirmDialog } from '../../ui/confirm-dialog'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import AudiencePicker from './AudiencePicker'
import RichTextEditor from './RichTextEditor'

const STATUS_VARIANT = { draft: 'muted', sending: 'blue', sent: 'teal', failed: 'orange' }

const STARTER_BODY = [
  '<p style="margin:0 0 16px;">Hi {{first_name}},</p>',
  '<p style="margin:0 0 16px;">Write your update here.</p>',
  '<p style="margin:0 0 16px;">— The dub team</p>',
].join('')

function CampaignList({ campaigns, onOpen, onNew, loading }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Campaigns</h2>
        <Button onClick={onNew}><Plus className="mr-2 h-4 w-4" />New campaign</Button>
      </div>

      {loading && <Loader2 className="mx-auto my-10 h-5 w-5 animate-spin text-muted-foreground" />}

      {!loading && campaigns.length === 0 && (
        <p className="rounded-lg border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
          No campaigns yet.
        </p>
      )}

      {!loading && campaigns.map((campaign) => (
        <button
          key={campaign.id}
          onClick={() => onOpen(campaign.id)}
          className="flex w-full items-center justify-between gap-4 rounded-lg border border-border px-4 py-3 text-left hover:bg-muted/50"
        >
          <span className="min-w-0">
            <span className="block truncate font-medium">{campaign.name}</span>
            <span className="block truncate text-sm text-muted-foreground">{campaign.subject}</span>
          </span>
          <span className="flex shrink-0 items-center gap-3 text-sm text-muted-foreground">
            {campaign.status === 'sent' && <span>{campaign.sent_count.toLocaleString()} sent</span>}
            <Badge variant={STATUS_VARIANT[campaign.status] ?? 'muted'}>{campaign.status}</Badge>
          </span>
        </button>
      ))}
    </div>
  )
}

export default function ComposeView({ onSelectCampaign }) {
  const notify = useNotification()
  const [campaigns, setCampaigns] = useState([])
  const [listLoading, setListLoading] = useState(true)
  const [openId, setOpenId] = useState(null)
  const [draft, setDraft] = useState(null)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [sending, setSending] = useState(false)
  const [progress, setProgress] = useState(null)
  const [confirmSend, setConfirmSend] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const loadList = useCallback(async () => {
    setListLoading(true)
    try {
      setCampaigns(await fetchCampaigns())
    } catch (err) {
      notify.error(err.message || 'Could not load campaigns')
    } finally {
      setListLoading(false)
    }
  }, [notify])

  useEffect(() => { if (!openId) loadList() }, [openId, loadList])

  useEffect(() => {
    if (!openId) { setDraft(null); return }
    fetchCampaign(openId)
      .then(setDraft)
      .catch((err) => notify.error(err.message || 'Could not open that campaign'))
  }, [openId, notify])

  const startNew = async () => {
    try {
      const created = await createCampaign({
        name: 'Untitled campaign',
        subject: '',
        body_html: STARTER_BODY,
        audience: DEFAULT_AUDIENCE,
      })
      setOpenId(created.id)
    } catch (err) {
      notify.error(err.message || 'Could not create a campaign')
    }
  }

  // Editing a sent campaign would make the Results view describe something
  // that was never actually mailed.
  const locked = draft?.status === 'sent' || draft?.status === 'sending'

  const patch = (fields) => setDraft((current) => ({ ...current, ...fields }))

  const save = async (extra = {}) => {
    setSaving(true)
    try {
      const saved = await updateCampaign(draft.id, {
        name: draft.name,
        subject: draft.subject,
        preheader: draft.preheader,
        // The editor normalises on blur, but a save can happen while it still
        // has focus — never persist raw contenteditable markup.
        body_html: sanitizeEmailHtml(draft.body_html),
        audience: draft.audience,
        ...extra,
      })
      setDraft(saved)
      return saved
    } finally {
      setSaving(false)
    }
  }

  const runTest = async () => {
    if (!draft.subject.trim()) { notify.error('Give the campaign a subject first'); return }
    setTesting(true)
    try {
      await save()
      const result = await sendTestEmail(draft.id)
      if (result?.setupRequired) { notify.warning(result.error || 'Email is not configured'); return }
      notify.success(`Test sent to ${result.sentTo}`)
      setDraft(await fetchCampaign(draft.id))
    } catch (err) {
      notify.error(err.message || 'Could not send the test')
    } finally {
      setTesting(false)
    }
  }

  // crm-send works in batches and returns before the platform's wall clock
  // kills it, so the real send is a loop here until it reports done. The
  // stall guard is the important part: if a call comes back having sent
  // nothing and cleared nothing, looping again would just repeat that forever.
  const runSend = async () => {
    setSending(true)
    setProgress({ sent: 0, remaining: null })
    try {
      await save()
      let previousRemaining = null
      for (;;) {
        const result = await sendCampaign(draft.id)
        if (result?.setupRequired) { notify.warning(result.error || 'Email is not configured'); break }
        setProgress({ sent: result.sent_count, remaining: result.remaining })

        if (!result.done && result.remaining === previousRemaining) {
          notify.error(`Sending stalled with ${result.remaining.toLocaleString()} left — nothing further was mailed`)
          setDraft(await fetchCampaign(draft.id))
          break
        }
        previousRemaining = result.remaining

        if (result.done) {
          notify.success(`Sent to ${result.sent_count.toLocaleString()} recipients`)
          if (result.failed_count) notify.warning(`${result.failed_count} could not be delivered`)
          setDraft(await fetchCampaign(draft.id))
          onSelectCampaign?.(draft.id, 'results')
          break
        }
      }
    } catch (err) {
      notify.error(err.message || 'Sending failed')
      setDraft(await fetchCampaign(draft.id).catch(() => draft))
    } finally {
      setSending(false)
      setProgress(null)
    }
  }

  const stopSend = async () => {
    try {
      await cancelCampaign(draft.id)
      notify.success('Stopped — remaining recipients were not mailed')
      setDraft(await fetchCampaign(draft.id))
    } catch (err) {
      notify.error(err.message || 'Could not stop the send')
    }
  }

  const removeCampaign = async () => {
    try {
      await deleteCampaign(draft.id)
      notify.success('Campaign deleted')
      setOpenId(null)
    } catch (err) {
      notify.error(err.message || 'Could not delete that campaign')
    }
  }

  if (!openId) {
    return <CampaignList campaigns={campaigns} loading={listLoading} onOpen={setOpenId} onNew={startNew} />
  }

  if (!draft) return <Loader2 className="mx-auto my-16 h-6 w-6 animate-spin text-muted-foreground" />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" onClick={() => setOpenId(null)}>← All campaigns</Button>
        <span className="flex items-center gap-2">
          <Badge variant={STATUS_VARIANT[draft.status] ?? 'muted'}>{draft.status}</Badge>
          {draft.status === 'draft' && (
            <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </span>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1fr_360px]">
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="crm-name">Internal name</Label>
            <Input id="crm-name" value={draft.name} disabled={locked}
              onChange={(e) => patch({ name: e.target.value })} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="crm-subject">Subject</Label>
            <Input id="crm-subject" value={draft.subject} disabled={locked}
              onChange={(e) => patch({ subject: e.target.value })}
              placeholder="What lands in the inbox" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="crm-preheader">Preview text <span className="text-muted-foreground">(optional)</span></Label>
            <Input id="crm-preheader" value={draft.preheader ?? ''} disabled={locked}
              onChange={(e) => patch({ preheader: e.target.value })}
              placeholder="The line shown after the subject in most inboxes" />
          </div>

          <div className="space-y-1.5">
            <Label id="crm-body-label">Body</Label>
            <RichTextEditor
              ariaLabel="Campaign body"
              value={draft.body_html}
              disabled={locked}
              onChange={(body_html) => patch({ body_html })}
            />
            <p className="text-xs text-muted-foreground">
              <strong>First name</strong> inserts the recipient&rsquo;s name, and
              falls back to &ldquo;there&rdquo; when we don&rsquo;t have one. The dub
              header, the unsubscribe link and the mailing address are added
              automatically — you don&rsquo;t need to write them.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <AudiencePicker
            audience={draft.audience ?? DEFAULT_AUDIENCE}
            onChange={(audience) => patch({ audience })}
            disabled={locked}
          />

          <div className="space-y-2 rounded-lg border border-border p-4">
            <div className="flex items-center gap-2 text-sm">
              {draft.test_sent_at
                ? <><Check className="h-4 w-4 text-[var(--teal-11)]" /><span>Test sent {new Date(draft.test_sent_at).toLocaleString()}</span></>
                : <span className="text-muted-foreground">Send yourself a test before the real thing.</span>}
            </div>

            <Button variant="outline" className="w-full" onClick={runTest} disabled={testing || locked}>
              {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <TestTube2 className="mr-2 h-4 w-4" />}
              Send test to me
            </Button>

            {draft.status === 'sending' ? (
              <Button variant="outline" className="w-full" onClick={stopSend}>Stop sending</Button>
            ) : (
              <Button
                className="w-full"
                onClick={() => setConfirmSend(true)}
                disabled={sending || locked || !draft.test_sent_at}
              >
                {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Send campaign
              </Button>
            )}

            {progress && (
              <p className="text-center text-xs text-muted-foreground">
                {progress.sent.toLocaleString()} sent
                {progress.remaining !== null && ` · ${progress.remaining.toLocaleString()} to go`}
              </p>
            )}

            <Button variant="ghost" size="sm" className="w-full" onClick={() => save().then(() => notify.success('Saved'))} disabled={saving || locked}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save draft
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmSend}
        onOpenChange={setConfirmSend}
        title="Send this campaign?"
        description="This mails the whole audience immediately and can't be undone. Recipients who unsubscribe mid-send are dropped automatically."
        confirmText="Send it"
        onConfirm={runSend}
      />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this campaign?"
        description="The draft and its audience settings are removed."
        confirmText="Delete"
        variant="destructive"
        onConfirm={removeCampaign}
      />
    </div>
  )
}
